import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useBlocker, useParams, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, ChevronDown, Eye, ExternalLink, Layers, Monitor, MoreHorizontal, PenLine, Plus, Redo2, Rocket, Save, Smartphone, Tablet, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { WebsiteRenderer } from "@baze/engine";
import type { Draft } from "@baze/api-client";
import { SECTION_META, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { Button, buttonClass } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Segmented } from "@/components/ui/segmented";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/States";
import { ApiError, api } from "@/lib/api";
import { qk } from "@/lib/query";
import { findTemplate, industryName, themeFor } from "@/lib/catalog";
import { useCatalog, useDraft, useSite } from "@/lib/queries";
import { cn } from "@/lib/cn";
import { useIsDesktop, useMediaQuery } from "@/lib/hooks";
import { session } from "@/lib/storage";
import { t } from "@/i18n/id";
import { useDocumentTitle } from "@/lib/document-title";
import { displayUrl } from "@/features/sites/SiteStatus";
import { DeviceFrame, type Device } from "@/features/preview/DeviceFrame";
import { Inspector } from "./inspector";
import { InlineTextEditor, textSpec, type InlineTarget } from "./inline-edit";
import { Outline } from "./outline";
import { AddSectionSheet, DockedTextEditor, SaveChip, SectionActionBar, SheetHandle, sheetClasses, type SheetSize } from "./mobile";
import { PublishDialog } from "./PublishDialog";
import { addPage, addSection, duplicateSection, locateSection, moveSection, removePage, removeSection, renamePage, reorderSections, setHidden, updateContent, type PagePreset } from "./model/ops";
import { getPath } from "./model/paths";
import type { DefaultsContext } from "./model/defaults";
import { commit, createHistory, redo, undo, type History } from "./model/history";
import { VerifyEmailBanner } from "@/components/shell/VerifyEmailBanner";

type SheetName = "outline" | "inspect" | "add" | null;

/** Quiet period after the last edit before the draft is saved automatically. */
const AUTOSAVE_MS = 1500;

export default function EditorPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const draft = useDraft(id);
  const site = useSite(id);
  const catalog = useCatalog();
  const qc = useQueryClient();
  const [history, setHistory] = useState<History<WebsiteDefinition> | null>(null);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>(() => (window.innerWidth < 1024 ? "mobile" : "desktop"));
  const [sheet, setSheetState] = useState<SheetName>(null);
  const [sheetSize, setSheetSize] = useState<SheetSize>("half");
  const setSheet = useCallback((next: SheetName | ((cur: SheetName) => SheetName)) => {
    setSheetState(next);
    setSheetSize("half");
  }, []);
  // Phones and tablets: panels are bottom sheets, text is typed in a bar docked above the keyboard.
  const isDesktop = useIsDesktop();
  const touch = useMediaQuery("(pointer: coarse)");
  const [previewMode, setPreviewMode] = useState(false);
  // Bumped by "Edit isi" so the inspector jumps to the Section tab even if another tab was open.
  const [inspectNonce, setInspectNonce] = useState(0);
  const bannerKey = `baze:editor-banner:${id}`;
  const [bannerHidden, setBannerHidden] = useState(() => session.get(bannerKey, false));
  const [bannerOpen, setBannerOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [inline, setInline] = useState<InlineTarget | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // The inline editor measures against the canvas element, so it is also kept in state.
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  const setCanvas = useCallback((el: HTMLDivElement | null) => {
    canvasRef.current = el;
    setCanvasEl(el);
  }, []);
  const hoverRef = useRef<string | null>(null);
  const revisionKey = draft.data ? `${id}:${draft.data.revision}` : "";
  const [loadedKey, setLoadedKey] = useState("");
  const [baseline, setBaseline] = useState("");
  if (draft.data && revisionKey !== loadedKey) {
    setLoadedKey(revisionKey);
    setBaseline(JSON.stringify(draft.data.definition));
    setHistory(createHistory(draft.data.definition));
    setRevision(draft.data.revision);
    setPageId((current) => current ?? draft.data?.definition.pages[0]?.id ?? null);
  }

  const def = history?.present;
  useDocumentTitle(site.data?.name ?? def?.site.name, t.overview.edit);
  const page = def?.pages.find((p) => p.id === pageId) ?? def?.pages[0];
  const section = selected && def ? locateSection(def, selected)?.section : undefined;
  const theme = useMemo(() => (def && catalog.data ? themeFor(catalog.data, def.theme) : null), [def, catalog.data]);
  // New sections and pages start from the site's own template content where it has some.
  const templateId = def?.meta?.templateId;
  const industry = def?.site.industry;
  const defaults = useMemo<DefaultsContext>(() => {
    const c = catalog.data;
    if (!c) return {};
    return {
      template: findTemplate(c, templateId) as DefaultsContext["template"],
      stock: c.stockImages,
      anchors: c.sectionAnchors,
      industryName: industry ? industryName(c, industry) : undefined,
    };
  }, [catalog.data, templateId, industry]);
  const dirty = !!def && JSON.stringify(def) !== baseline;
  const live = site.data?.status === "published";
  const pending = live && (dirty || !!site.data?.hasUnpublishedChanges);

  const save = useMutation({
    mutationFn: async (definition: WebsiteDefinition) => {
      const res = await api.sites.saveDraft(id, definition, revision);
      return { res, definition };
    },
    meta: { silent: true },
    onSuccess: ({ res, definition }) => {
      setLoadedKey(`${id}:${res.revision}`);
      setBaseline(JSON.stringify(definition));
      setRevision(res.revision);
      qc.setQueryData<Draft>(qk.sites.draft(id), (old) => (old ? { ...old, definition, revision: res.revision, updatedAt: res.updatedAt } : old));
      void qc.invalidateQueries({ queryKey: qk.sites.detail(id) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) void draft.refetch();
      else toast.error(t.editor.save.error);
    },
  });

  const apply = (next: WebsiteDefinition, key?: string) => {
    setHistory((h) => (h ? commit(h, next, key) : h));
  };

  const saveNow = useCallback(async () => {
    if (!def || JSON.stringify(def) === baseline) return;
    await save.mutateAsync(def);
  }, [baseline, def, save]);

  const select = (sectionId: string, scroll: boolean) => {
    setSelected(sectionId);
    if (sectionId !== inline?.sectionId) setInline(null);
    const loc = def ? locateSection(def, sectionId) : null;
    if (loc) setPageId(loc.page.id);
    if (scroll) {
      requestAnimationFrame(() => {
        // Below lg a sheet may cover the lower half: bring the section to the top of the canvas.
        canvasRef.current?.querySelector(`[data-baze-section="${CSS.escape(sectionId)}"]`)?.scrollIntoView({ block: isDesktop ? "nearest" : "start", behavior: "smooth" });
      });
    }
  };

  /** Open the inspector on the selected section (mobile "Edit isi"). */
  const editSelected = () => {
    setInspectNonce((n) => n + 1);
    setSheet("inspect");
  };

  /** Delete with an "Urungkan" toast; undo only applies while nothing else has changed since. */
  const deleteSection = useCallback(
    (sectionId: string) => {
      if (!def) return;
      const loc = locateSection(def, sectionId);
      if (!loc || loc.section.type === "navbar" || loc.section.type === "footer") return;
      const next = removeSection(def, sectionId);
      setHistory((h) => (h ? commit(h, next) : h));
      setSelected((cur) => (cur === sectionId ? null : cur));
      toast(t.editor.outline.deleted(typeLabel(loc.section.type)), {
        action: { label: t.editor.undo, onClick: () => setHistory((h) => (h && h.present === next ? undo(h) : h)) },
      });
    },
    [def],
  );

  const duplicate = (sectionId: string) => {
    if (!def) return;
    const res = duplicateSection(def, sectionId);
    if (!res.id) return;
    apply(res.def);
    select(res.id, true);
  };

  const publishFromUrl = params.get("publish") === "1";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName));
      if (meta && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty) void saveNow();
        return;
      }
      if (typing) return;
      if (e.key === "Escape") setSelected(null);
      if ((e.key === "Delete" || e.key === "Backspace") && selected && def) {
        e.preventDefault();
        deleteSection(selected);
      }
      if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown") && selected && def) {
        e.preventDefault();
        apply(moveSection(def, selected, e.key === "ArrowUp" ? -1 : 1));
      }
      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        setHistory((h) => (h ? (e.shiftKey ? redo(h) : undo(h)) : h));
      }
      if (meta && e.key.toLowerCase() === "y") {
        e.preventDefault();
        setHistory((h) => (h ? redo(h) : h));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, selected, def, revision, saveNow, deleteSection]);

  // Lets global styles (toast position) adapt while the editor is open.
  useEffect(() => {
    document.body.setAttribute("data-editor", "");
    return () => document.body.removeAttribute("data-editor");
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  // Autosave: shortly after the last edit. A failed save is not retried until the draft changes again
  // (the header shows the error and the Save button stays available).
  useEffect(() => {
    if (!def || !dirty || save.isPending || (save.isError && save.variables === def)) return;
    const timer = window.setTimeout(() => void saveNow().catch(() => undefined), AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [def, dirty, save.isPending, save.isError, save.variables, saveNow]);

  // Leaving with unsaved edits (e.g. right after typing): save first, ask only if that fails.
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    saveNow().then(
      () => blocker.proceed(),
      () => {
        if (window.confirm(t.editor.leaveWarning)) blocker.proceed();
        else blocker.reset();
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per blocked navigation
  }, [blocker.state]);

  const clearHover = () => {
    const root = canvasRef.current;
    if (root && hoverRef.current) root.querySelector(`[data-baze-section="${CSS.escape(hoverRef.current)}"]`)?.removeAttribute("data-hovered");
    hoverRef.current = null;
  };

  const onCanvasOver = (e: MouseEvent<HTMLDivElement>) => {
    const node = (e.target as HTMLElement).closest("[data-baze-section]");
    const next = node?.getAttribute("data-baze-section") ?? null;
    if (next === hoverRef.current) return;
    clearHover();
    hoverRef.current = next;
    if (next) node?.setAttribute("data-hovered", "");
  };

  /** Start editing a text field element in place. False when it is not plain editable text. */
  const startInline = (fieldEl: HTMLElement): boolean => {
    if (!def) return false;
    const sectionId = fieldEl.closest("[data-baze-section]")?.getAttribute("data-baze-section");
    const path = fieldEl.getAttribute("data-field");
    const loc = sectionId ? locateSection(def, sectionId) : null;
    const spec = loc && path ? textSpec(loc.section.type, path) : null;
    const current = loc && path ? getPath(loc.section, path) : undefined;
    if (!sectionId || !path || !spec || typeof current !== "string") return false;
    select(sectionId, false);
    setInline({ sectionId, path, initial: current, spec });
    // Touch: the docked bar and keyboard cover the lower screen; keep the text in view above them.
    if (touch) requestAnimationFrame(() => fieldEl.scrollIntoView({ block: "start", behavior: "smooth" }));
    return true;
  };

  /** Tab / Shift+Tab inside the in-place editor: the next editable text in page order, else close. */
  const moveInline = (dir: 1 | -1) => {
    const root = canvasRef.current;
    if (!root || !inline) return setInline(null);
    const all = [...root.querySelectorAll<HTMLElement>("[data-baze-section] [data-field]")].filter((el) => el.getClientRects().length > 0);
    const at = all.findIndex((el) => el.getAttribute("data-field") === inline.path && el.closest("[data-baze-section]")?.getAttribute("data-baze-section") === inline.sectionId);
    for (let i = at + dir; i >= 0 && i < all.length; i += dir) {
      const el = all[i]!;
      if (startInline(el)) {
        el.scrollIntoView({ block: "nearest" });
        return;
      }
    }
    setInline(null);
  };

  const onCanvasClick = (e: MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    const anchor = el.closest("a");
    if (anchor) e.preventDefault();
    if (previewMode) return;
    const node = el.closest("[data-baze-section]");
    const sectionId = node?.getAttribute("data-baze-section");
    if (!sectionId || !def) return;
    // Text marked by the engine is edited right where it is; anything else opens the inspector.
    const fieldEl = el.closest<HTMLElement>("[data-field]");
    if (fieldEl && node?.contains(fieldEl) && startInline(fieldEl)) {
      // FAQ questions sit in <summary>: edit the text instead of toggling the answer.
      if (el.closest("summary")) e.preventDefault();
      return;
    }
    select(sectionId, false);
  };

  if (draft.isPending || catalog.isPending || !history || !def || !page) {
    if (draft.isError || catalog.isError) return <ErrorState error={draft.error ?? catalog.error} onRetry={() => void draft.refetch()} />;
    return <Skeleton className="m-6 h-[70vh] rounded-2xl" />;
  }

  const url = site.data?.primaryUrl;
  /** Insert right after the selected section on this page; otherwise before the footer. */
  const addOfType = (type: SectionType) => {
    const at = selected ? page.sections.findIndex((s) => s.id === selected) : -1;
    const res = addSection(def, page.id, type, at >= 0 ? at + 1 : undefined, defaults);
    if (!res.id) return;
    apply(res.def);
    if (!isDesktop) setSheet(null);
    select(res.id, true);
    toast.success(t.editor.picker.added(typeLabel(type)));
  };
  const inlineSection = inline ? locateSection(def, inline.sectionId)?.section : undefined;
  const inlineValue = inlineSection && inline ? (getPath(inlineSection, inline.path) as string | undefined) : undefined;
  const outline = (
    <Outline
      siteId={id}
      def={def}
      pageId={page.id}
      pageLimit={site.data?.plan.limits.pagesPerSite}
      sections={page.sections}
      selected={selected}
      onPage={(next) => { setPageId(next); setSelected(null); }}
      onAddPage={(preset: PagePreset, title: string) => {
        const res = addPage(def, preset, title, defaults);
        apply(res.def);
        setPageId(res.pageId);
        setSelected(null);
        toast.success(t.editor.pageAdded(title));
      }}
      onRenamePage={(pageId, title) => apply(renamePage(def, pageId, title))}
      onRemovePage={(pageId) => {
        const removed = def.pages.find((p) => p.id === pageId);
        const next = removePage(def, pageId);
        if (!removed || next === def) return;
        apply(next);
        setSelected(null);
        setPageId(next.pages.find((p) => p.slug === "/")?.id ?? next.pages[0]?.id ?? null);
        toast(t.editor.pageRemoved(removed.title), {
          action: {
            label: t.editor.undo,
            onClick: () => {
              setHistory((h) => (h && h.present === next ? undo(h) : h));
              setPageId(pageId);
            },
          },
        });
      }}
      onReorder={(from, to) => apply(reorderSections(def, page.id, from, to))}
      onSelect={(sectionId) => {
        select(sectionId, true);
        if (!isDesktop) setSheet(null);
      }}
      onAdd={addOfType}
      onMove={(sectionId, delta) => apply(moveSection(def, sectionId, delta))}
      onHide={(sectionId, hidden) => apply(setHidden(def, sectionId, hidden))}
      onDelete={deleteSection}
    />
  );
  const inspector = (
    <Inspector
      siteId={id}
      siteUrl={url}
      def={def}
      pageId={page.id}
      section={section}
      apply={apply}
      onMove={(sectionId, delta) => apply(moveSection(def, sectionId, delta))}
      onDuplicate={duplicate}
      onDelete={deleteSection}
      focusSection={inspectNonce}
    />
  );

  const saveState = save.isPending ? "saving" : save.isError ? "error" : dirty ? "dirty" : "saved";
  const showBanner = !bannerHidden && ((!live && !!url) || pending);
  const showActionBar = !isDesktop && !!section && !sheet && !inline && !previewMode;
  const saveText = save.isPending ? t.editor.save.saving : save.isError ? t.editor.save.error : dirty ? t.editor.save.dirty : t.editor.save.savedNow;

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[#eef1f4]">
      <header className="safe-top z-40 flex h-14 shrink-0 items-center gap-1 border-b border-line bg-white px-2 sm:gap-2 sm:px-3">
        <Link to={`/situs/${id}`} className={buttonClass("ghost", "sm", "max-w-[34vw] max-sm:px-2.5 sm:max-w-[200px]")} aria-label={t.editor.back}>
          <ArrowLeft aria-hidden="true" />
          <span className="truncate max-sm:sr-only">{site.data?.name ?? t.editor.back}</span>
        </Link>
        <SaveChip state={saveState} onRetry={() => void saveNow().catch(() => undefined)} />
        {site.data ? (
          <Badge tone={live ? "success" : "warning"} dot className="max-sm:hidden">
            {live ? t.editor.status.live : t.editor.status.draft}
          </Badge>
        ) : null}
        <Button variant="ghost" size="icon-sm" disabled={!history.past.length} onClick={() => setHistory((h) => (h ? undo(h) : h))} aria-label={t.editor.undo}>
          <Undo2 aria-hidden="true" />
        </Button>
        <Button variant="ghost" size="icon-sm" className="max-sm:hidden" disabled={!history.future.length} onClick={() => setHistory((h) => (h ? redo(h) : h))} aria-label={t.editor.redo}>
          <Redo2 aria-hidden="true" />
        </Button>
        <p className={cn("hidden min-w-0 truncate text-xs font-medium md:block", save.isError ? "text-danger" : dirty ? "text-amber-700" : "text-slate-400")} aria-live="polite">
          {dirty ? null : <Check className="mr-1 inline size-3.5" aria-hidden="true" />}
          {saveText}
        </p>
        <div className="ml-auto flex items-center gap-1.5">
          {live && url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className={buttonClass("ghost", "sm", "max-lg:hidden")}>
              <ExternalLink aria-hidden="true" />
              {displayUrl(url)}
            </a>
          ) : null}
          <Link to={`/situs/${id}/pratinjau`} className={buttonClass("ghost", "sm", "max-sm:hidden")}>
            <Eye aria-hidden="true" />
            {t.editor.preview}
          </Link>
          {dirty || save.isPending || save.isError ? (
            <Button variant="secondary" size="sm" className="max-sm:hidden" onClick={() => void saveNow()} disabled={!dirty || save.isPending} loading={save.isPending} leftIcon={<Save aria-hidden="true" />}>
              {t.common.save}
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="sm:hidden" aria-label={t.editor.mobile.more}>
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!history.future.length} onSelect={() => setHistory((h) => (h ? redo(h) : h))}>
                <Redo2 aria-hidden="true" />
                {t.editor.redo}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/situs/${id}/pratinjau`}>
                  <Eye aria-hidden="true" />
                  {t.editor.mobile.fullPreview}
                </Link>
              </DropdownMenuItem>
              {live && url ? (
                <DropdownMenuItem asChild>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink aria-hidden="true" />
                    {t.editor.mobile.openLive}
                  </a>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem disabled={!dirty || save.isPending} onSelect={() => void saveNow().catch(() => undefined)}>
                <Save aria-hidden="true" />
                {t.editor.mobile.saveNow}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={() => setPublishOpen(true)}>
            <Rocket aria-hidden="true" />
            {t.editor.publish}
          </Button>
        </div>
      </header>
      <VerifyEmailBanner />

      {showBanner ? (
        <div className="flex shrink-0 items-start gap-2 border-b border-amber-200/80 bg-amber-50 py-1.5 pr-1 pl-3 text-[13px] leading-snug text-amber-950 sm:items-center sm:py-2 sm:pl-4">
          <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-amber-500 sm:mt-0" aria-hidden="true" />
          <button type="button" className="min-w-0 flex-1 py-0.5 text-left sm:pointer-events-none" onClick={() => setBannerOpen((o) => !o)} aria-expanded={bannerOpen}>
            <span className={cn("block", !bannerOpen && "max-sm:truncate")}>
              <span className="font-semibold">{!live && url ? t.editor.liveBar.draftTitle : t.editor.liveBar.pendingTitle}.</span>{" "}
              {!live && url ? t.editor.liveBar.draftBody(displayUrl(url)) : t.editor.liveBar.pendingBody}
            </span>
          </button>
          {!bannerOpen ? <ChevronDown className="mt-[3px] size-4 shrink-0 text-amber-700 sm:hidden" aria-hidden="true" /> : null}
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-full text-amber-800 hover:bg-amber-100"
            aria-label={t.editor.mobile.dismissBanner}
            onClick={() => {
              setBannerHidden(true);
              session.set(bannerKey, true);
            }}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1">
        {sheet && sheetSize === "full" ? (
          <button type="button" className="fixed inset-0 z-30 bg-slate-950/40 lg:hidden" aria-label={t.common.close} onClick={() => setSheet(null)} />
        ) : null}
        <aside className={cn("z-40 flex min-h-0 w-[272px] shrink-0 flex-col border-r border-line bg-white", sheetClasses(sheet === "outline", sheetSize))}>
          <SheetHandle title={t.editor.outline.title} size={sheetSize} onSize={setSheetSize} onClose={() => setSheet(null)} />
          <div className="min-h-0 flex-1 max-lg:pt-2">{outline}</div>
        </aside>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 px-3 py-2.5 max-sm:hidden sm:px-4">
            <p className="hidden min-w-0 truncate text-xs font-medium text-slate-500 sm:block" aria-live="polite">
              {inline ? (
                <>
                  <span className="font-semibold text-primary">{t.editor.inline.editing}</span> · {inline.spec.multiline ? t.editor.inline.hintMulti : t.editor.inline.hint} · {(inlineValue ?? "").length}/{inline.spec.max}
                </>
              ) : (
                t.editor.canvasHint
              )}
            </p>
            <div className="ml-auto">
              <Segmented
                size="sm"
                label={t.editor.deviceLabel}
                value={device}
                onChange={setDevice}
                options={[
                  { value: "mobile", label: <Smartphone aria-hidden="true" />, ariaLabel: t.editor.device.mobile },
                  { value: "tablet", label: <Tablet aria-hidden="true" />, ariaLabel: t.editor.device.tablet },
                  { value: "desktop", label: <Monitor aria-hidden="true" />, ariaLabel: t.editor.device.desktop },
                ]}
              />
            </div>
          </div>
          <div
            className={cn(
              "scrollbar-thin min-h-0 flex-1 overflow-auto bg-[radial-gradient(circle_at_1px_1px,rgb(148_163_184/0.45)_1px,transparent_0)] bg-[length:18px_18px] sm:px-6 lg:pb-8",
              // Room to scroll the last section above whatever covers the bottom (sheet, action bar, nav).
              sheet ? "pb-[60dvh]" : showActionBar ? "pb-44" : inline && touch ? "pb-[50dvh]" : "pb-20",
            )}
          >
            {theme ? (
              // On phones the canvas is the phone: no bezel or margins, the site uses the full width.
              <DeviceFrame device={device} flushOnPhone>
                <div className="flex items-center gap-2 border-b border-slate-200/80 bg-slate-50 px-3 py-2">
                  <span className="flex gap-1" aria-hidden="true">
                    <i className="size-2 rounded-full bg-slate-300" />
                    <i className="size-2 rounded-full bg-slate-300" />
                    <i className="size-2 rounded-full bg-slate-300" />
                  </span>
                  <span className="mx-auto flex max-w-[70%] items-center gap-1.5 truncate rounded-full bg-white px-3 py-1 text-[11px] font-medium text-slate-500 ring-1 ring-slate-900/5">
                    {url ? displayUrl(url) : page.title}
                    <Badge tone="neutral" className="h-5 shrink-0 px-1.5 text-[10px]">{t.editor.preview}</Badge>
                  </span>
                </div>
                <div ref={setCanvas} className="baze-canvas relative" data-preview={previewMode ? "" : undefined} onMouseOver={onCanvasOver} onMouseLeave={clearHover} onClick={onCanvasClick}>
                  <WebsiteRenderer definition={def} theme={theme} pageSlug={page.slug} mode="editor" siteId={id} branding={site.data ? !site.data.plan.limits.removeBranding || site.data.keepBranding : true} selectedSectionId={previewMode ? null : selected} idPrefix="ed-" />
                  {inline && canvasEl && !touch ? (
                    <InlineTextEditor
                      key={`${inline.sectionId}:${inline.path}`}
                      root={canvasEl}
                      target={inline}
                      value={inlineValue ?? ""}
                      onChange={(v) => apply(updateContent(def, inline.sectionId, inline.path, v), `${inline.sectionId}:${inline.path}`)}
                      onDone={() => setInline(null)}
                      onTab={moveInline}
                    />
                  ) : null}
                </div>
              </DeviceFrame>
            ) : (
              <Skeleton className="h-96 rounded-2xl" />
            )}
          </div>
        </main>
        <aside className={cn("z-40 flex min-h-0 w-[340px] shrink-0 flex-col border-l border-line bg-white", sheetClasses(sheet === "inspect", sheetSize))}>
          <SheetHandle title={section ? typeLabel(section.type) : t.editor.tabs.info} size={sheetSize} onSize={setSheetSize} onClose={() => setSheet(null)} />
          <div className="min-h-0 flex-1">{inspector}</div>
        </aside>
      </div>

      <AddSectionSheet open={sheet === "add"} page={page} size={sheetSize} onSize={setSheetSize} onClose={() => setSheet(null)} onAdd={addOfType} />

      {showActionBar && section ? (
        <SectionActionBar
          type={section.type}
          hidden={!!section.hidden}
          pinned={section.type === "navbar" || section.type === "footer"}
          canUp={moveSection(def, section.id, -1) !== def}
          canDown={moveSection(def, section.id, 1) !== def}
          onUp={() => apply(moveSection(def, section.id, -1))}
          onDown={() => apply(moveSection(def, section.id, 1))}
          onDuplicate={() => duplicate(section.id)}
          onHide={() => apply(setHidden(def, section.id, !section.hidden))}
          onDelete={() => deleteSection(section.id)}
          onEdit={editSelected}
          onClose={() => setSelected(null)}
        />
      ) : null}

      {inline && touch && inlineSection ? (
        <DockedTextEditor
          key={`${inline.sectionId}:${inline.path}`}
          target={inline}
          sectionType={inlineSection.type}
          value={inlineValue ?? ""}
          onChange={(v) => apply(updateContent(def, inline.sectionId, inline.path, v), `${inline.sectionId}:${inline.path}`)}
          onDone={() => setInline(null)}
          onNext={() => moveInline(1)}
        />
      ) : null}

      {previewMode ? (
        <button
          type="button"
          onClick={() => setPreviewMode(false)}
          className="fixed top-[calc(4rem+env(safe-area-inset-top))] left-1/2 z-40 flex h-10 -translate-x-1/2 items-center gap-2 rounded-full whitespace-nowrap bg-slate-900/90 px-4 text-xs font-semibold text-white shadow-lg backdrop-blur lg:hidden"
        >
          <Eye className="size-4" aria-hidden="true" />
          {t.editor.mobile.previewing} · <span className="underline underline-offset-2">{t.editor.mobile.backToEdit}</span>
        </button>
      ) : null}

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-50 grid grid-cols-4 border-t border-line bg-white lg:hidden" aria-label={t.editor.mobile.nav}>
        <NavButton active={sheet === "outline"} label={t.editor.mobile.sections} onClick={() => { setPreviewMode(false); setSheet((s) => (s === "outline" ? null : "outline")); }}>
          <Layers aria-hidden="true" />
        </NavButton>
        <NavButton active={sheet === "add"} label={t.editor.mobile.add} primary onClick={() => { setPreviewMode(false); setInline(null); setSheet((s) => (s === "add" ? null : "add")); }}>
          <Plus aria-hidden="true" />
        </NavButton>
        <NavButton active={sheet === "inspect"} label={t.editor.mobile.edit} onClick={() => { setPreviewMode(false); setSheet((s) => (s === "inspect" ? null : "inspect")); }}>
          <PenLine aria-hidden="true" />
        </NavButton>
        <NavButton
          active={previewMode}
          label={t.editor.mobile.preview}
          onClick={() => {
            setSheet(null);
            setInline(null);
            clearHover();
            setPreviewMode((p) => !p);
          }}
        >
          <Eye aria-hidden="true" />
        </NavButton>
      </nav>

      <PublishDialog
        open={publishOpen || publishFromUrl}
        onOpenChange={(next) => {
          setPublishOpen(next);
          if (!next && publishFromUrl) {
            const cleared = new URLSearchParams(params);
            cleared.delete("publish");
            setParams(cleared, { replace: true });
          }
        }}
        siteId={id}
        dirty={dirty}
        save={saveNow}
        onFix={(issue) => {
          if (issue.pageId) setPageId(issue.pageId);
          if (issue.sectionId) select(issue.sectionId, true);
          setPublishOpen(false);
        }}
      />
    </div>
  );
}

function NavButton({ active, primary, label, onClick, children }: { active: boolean; primary?: boolean; label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn("flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold [&_svg]:size-5", active ? "text-primary" : "text-slate-600")}
    >
      {/* Same-height icon slot in every column so the labels share one baseline. */}
      <span className="grid h-7 place-items-center">
        {primary ? (
          <span className={cn("grid size-7 place-items-center rounded-full shadow-sm transition-transform [&_svg]:size-4", active ? "rotate-45 bg-slate-900 text-white" : "bg-primary text-white")}>{children}</span>
        ) : (
          children
        )}
      </span>
      {label}
    </button>
  );
}

function typeLabel(type: SectionType) {
  return SECTION_META[type].label;
}
